/**
 * Campaign Games Grid Component
 * Renders only the games grid for a campaign (no panel/header)
 */

import { type FC, memo, useMemo } from 'react';

import { SECTION_NAMES } from '@repo/common';

import type IGameSettings from 'types/models/IGameSettings';
import type ISectionSettings from 'types/models/ISectionSettings';

import ReactVirtualGrid from 'shared/components/VirtualizedGrid';
import { resolveCampaignGames } from 'features/campaigns/utils';
import { useAppStore, useViewMode } from 'store/app/appStore';

import styles from './CampaignGamesGrid.module.css';

type CampaignGamesSource = { games: string[] } | { games: IGameSettings[] };

interface CampaignGamesGridProps {
  data: ISectionSettings;
  campaign: CampaignGamesSource;
  isActive?: boolean;
}

const CampaignGamesGrid: FC<CampaignGamesGridProps> = ({ data, campaign }) => {
  const supportedGames = useAppStore((state) => state.supportedGames);

  const viewMode = useViewMode();

  const topGames = useAppStore((state) => state.sections.find((s) => s.name === SECTION_NAMES.TOP)?.games);

  /** Convert campaign or tournament games to full game objects */
  const games = useMemo(() => {
    if (!campaign.games.length) return [];

    const first = (campaign.games as (string | IGameSettings)[])[0];

    // PrizePop / Campaign: games is string[] of game keys
    if (typeof first === 'string') {
      const topGameKeys = new Set(topGames?.map((g) => g.gameKey) ?? []);

      return resolveCampaignGames(campaign.games as string[], supportedGames, topGameKeys);
    }

    // Tournament: games is already IGameSettings[]
    return campaign.games as IGameSettings[];
  }, [campaign.games, supportedGames, topGames]);

  return (
    <div className={styles.scrollableCampaignGridWrapper}>
      <div key={`campaign-grid-${viewMode}`} className={styles.campaignGamesGrid} data-view="multi" data-one-item="true">
        <ReactVirtualGrid games={games} isFav={false} sectionName={data.name} hasJackpotPanel={false} />
      </div>
    </div>
  );
};

export default memo(CampaignGamesGrid);
